<template>
    <b-modal
        ref="modalNewTemplate"
        lazy
        title-tag="h2"
        :title="modalTitle"
        ok-title="Save"
        footer-class="d-flex"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-alert show variant="warning" v-if="displayHashtagAlert">{{
            hashtagAlertMessage
        }}</b-alert>
        <b-form-group
            v-if="!isNext"
            :label-for="`Type`"
            :label-cols="3"
            label="Type:"
        >
            <b-form-select
                v-if="!isEditMode"
                id="type"
                v-model="data.type"
                :options="sortedTypeOptions"
                name="type"
                :disabled="isLoadingTypeList || isLoading"
            >
                <template #first>
                    <option v-if="isLoadingTypeList" disabled value="null"
                        >Loading...</option
                    >
                </template>
            </b-form-select>

            <template v-else>
                <span
                    v-if="data.type == TEMPLATES.TYPE.ATTACHMENT"
                    class="d-flex mt-2"
                    >Attachment</span
                >
                <span
                    v-else-if="data.type == TEMPLATES.TYPE.CUSTOM_FIELD"
                    class="d-flex mt-2"
                    >Custom Field</span
                >
            </template>
        </b-form-group>

        <b-form-group
            label-for="title"
            :label-cols="isNext ? '4' : '3'"
            :label="isNext ? 'Name:' : 'Title:'"
        >
            <b-input
                v-model="data.name"
                name="name"
                autofocus
                type="text"
                placeholder="Title (required)"
                :disabled="isLoading"
                v-validate="'required'"
                :class="{
                    'border-danger':
                        (errors.has('name') || isNameExceed) &&
                        data.type == TEMPLATES.TYPE.WEBFORM_TEMPLATE
                }"
            />
            <span
                v-if="data.type == TEMPLATES.TYPE.WEBFORM_TEMPLATE"
                v-bind:style="[
                    nameCount >= 0 ? { color: 'grey' } : { color: 'red' }
                ]"
                style="font-size: 10px;"
                class="mt-1 d-block text-right"
            >
                {{ nameCount }} character(s) remaining
            </span>
        </b-form-group>

        <template v-if="!isNext">
            <b-form-group
                v-if="data.type == TEMPLATES.TYPE.CONTENT_SNIPPET"
                label-for="hashtag"
                label-cols="3"
                label="Hashtag:"
            >
                <b-input
                    v-model="data.hashtag"
                    name="hashtag"
                    type="text"
                    placeholder="Hashtag (optional)"
                    :disabled="isLoading"
                />
            </b-form-group>
            <b-form-group
                v-if="data.type != TEMPLATES.TYPE.CUSTOM_FIELD"
                label-for="description"
                label-cols="3"
                label="Description:"
            >
                <b-textarea
                    v-model="data.description"
                    name="description"
                    placeholder="Description (optional)"
                    :disabled="isLoading"
                />
            </b-form-group>
            <b-form-group
                v-if="data.type == TEMPLATES.TYPE.ATTACHMENT && !isEditMode"
                label-for="attachment"
                label-cols="3"
                label="Attachment:"
            >
                <b-form
                    v-model="data.file"
                    enctype="multipart/form-data"
                    class="pt-1"
                >
                    <input
                        id="file"
                        ref="file"
                        type="file"
                        class="input-file"
                    />
                </b-form>
            </b-form-group>
            <template v-if="data.type == TEMPLATES.TYPE.CUSTOM_FIELD">
                <b-form-group label-for="values" label-cols="3" label="Values:">
                    <b-textarea
                        v-model="data.valuess"
                        name="values"
                        placeholder="Enter one value per line (optional)"
                        :disabled="isLoading"
                    />
                    <b-form-checkbox
                        id="allow-multiple-selections"
                        name="allow-multiple-selections"
                        v-model="data.is_multiple"
                        class="mt-1 pe-auto"
                        :value="1"
                        :unchecked-value="0"
                    >
                        <span
                            class="font-weight-normal"
                            style="font-size: 12px;"
                            >Allow multiple selections</span
                        >
                    </b-form-checkbox>
                </b-form-group>
                <b-form-group
                    label-for="category"
                    label-cols="3"
                    label="Include In:"
                >
                    <div class="pt-1">
                        <b-form-select
                            id="category"
                            :options="categoryOptions"
                            name="category"
                            v-model="data.category"
                            :disabled="isLoadingCategoryList || isLoading"
                        ></b-form-select>
                    </div>
                    <div v-if="componentOptions.length > 1" class="pt-2">
                        <b-form-select
                            id="component"
                            :options="componentOptions"
                            name="component"
                            v-model="data.component"
                            :disabled="isLoadingComponentList || isLoading"
                        ></b-form-select>
                    </div>
                    <div v-if="subComponentOptions.length > 1" class="pt-2">
                        <b-form-select
                            id="subComponent"
                            :options="subComponentOptions"
                            name="subComponent"
                            v-model="data.subComponent"
                            :disabled="isLoadingSubComponentList || isLoading"
                        ></b-form-select>
                    </div>
                </b-form-group>
            </template>
        </template>

        <template
            v-if="isNext && this.data.type == TEMPLATES.TYPE.WELLNESS_PLAN"
        >
            <b-form-group
                label-for="plan_term"
                label-cols="4"
                label="Plan Term:"
            >
                <div class="pt-1">
                    <b-form-select
                        id="plan_term"
                        :options="planTermOptions"
                        name="plan_term"
                        v-model="data.plan_term"
                    ></b-form-select>
                </div>
            </b-form-group>
            <b-form-group
                label-for="enrollment_fee"
                label-cols="4"
                label="Enrollment Fee:"
            >
                <vet-select
                    module="item"
                    placeholder="Enrollment Fee (optional)"
                    name="item"
                    v-model="selectedEnrollmentFee"
                    :selectProps="vetItemProps"
                    :isAddItemFee="true"
                />
            </b-form-group>
            <b-form-group label-for="plan_fee" label-cols="4" label="Plan Fee:">
                <vet-select
                    module="item"
                    placeholder="Fee (required)"
                    name="item"
                    v-model="selectedPlanFee"
                    v-validate="'required'"
                    :selectProps="vetItemProps"
                    :class="{
                        'border-danger': errors.has('item')
                    }"
                    :showMenu="false"
                />
            </b-form-group>
            <b-form-group
                label-for="payment_terms"
                label-cols="4"
                label="Payment Terms:"
            >
                <div class="pt-1">
                    <b-form-select
                        id="payment_terms"
                        :options="paymentTermOptions"
                        name="payment_terms"
                        v-model="data.payment_terms"
                    ></b-form-select>
                </div>
            </b-form-group>
            <b-form-group
                label-for="production_credit"
                label-cols="4"
                label="Production Credit:"
            >
                <div class="pt-1">
                    <b-form-select
                        id="production_credit"
                        :options="productionCreditOptions"
                        name="production_credit"
                        v-model="data.production_credit"
                    ></b-form-select>
                </div>
            </b-form-group>
            <b-form-group
                label-for="enrollment"
                label-cols="4"
                label="Enrollment Goal:"
            >
                <div class="d-flex">
                    <b-input
                        v-model="data.enrollment_goal_value"
                        name="enrollment_goal_value"
                        type="number"
                        placeholder=""
                        :disabled="isLoading"
                        autocomplete="off"
                        class="w-50"
                        v-validate="'numericalWithDecimal|decimal:2'"
                        :class="{
                            'border-danger': errors.has('enrollment_goal_value')
                        }"
                    />
                    <span class="px-3 d-flex align-items-center text-nowrap"
                        >% of</span
                    >
                    <b-form-select
                        id="enrollment_goal_id"
                        :options="enrollmentGoalOptions"
                        name="enrollment_goal_id"
                        v-model="data.enrollment_goal_id"
                        class="w-100"
                    ></b-form-select>
                </div>
            </b-form-group>
        </template>

        <template v-slot:modal-footer="{ close }">
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <!-- save button for ADD mode ATTACHMENT + CUSTOM FIELD-->
            <b-button-group
                v-if="
                    !isLoading &&
                    !isEditMode &&
                    (data.type == TEMPLATES.TYPE.ATTACHMENT ||
                        data.type == TEMPLATES.TYPE.CUSTOM_FIELD)
                "
            >
                <b-dropdown
                    v-if="!continues"
                    @click="proceed(false)"
                    left
                    split
                    text="Save + Done"
                    variant="primary"
                    :disabled="data.name === ''"
                >
                    <b-dropdown-item @click="proceed(true)">
                        Save + Add New
                    </b-dropdown-item>
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="proceed(true)"
                    left
                    split
                    text="Save + Add New"
                    variant="primary"
                    :disabled="data.name === ''"
                >
                    <b-dropdown-item @click="proceed(false)">
                        Save + Done
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>

            <template v-else>
                <b-button
                    v-if="
                        !isLoading &&
                        !isNext &&
                        data.type == TEMPLATES.TYPE.WELLNESS_PLAN
                    "
                    variant="primary"
                    @click="isNext = !isNext"
                    :disabled="data.name === ''"
                >
                    Next
                </b-button>
                <b-button
                    v-if="
                        !isLoading &&
                        (data.type != TEMPLATES.TYPE.WELLNESS_PLAN || isNext)
                    "
                    variant="primary"
                    @click="proceed"
                    :disabled="data.name === '' || displayHashtagAlert"
                >
                    Save
                </b-button>
            </template>
        </template>
    </b-modal>
</template>

<script>
import TemplateService from "@/services/templates";
import AttachmentService from "@/services/attachment";
import TEMPLATES from "@/constants/templates";
import AWSs3Mixin from "@/mixins/awss3.js";
import MIMES from "@/module/commons/mimes.json";
import { map, findKey, forEach, get, isEmpty } from "lodash";

const RESERVED_KEYWORDS = ["date", "time"];

export default {
    mixins: [AWSs3Mixin],
    components: {
        Loader: () => import("@/module/commons/loader"),
        VetSelect: () => import("@/module/commons/vet-select")
    },
    data() {
        return {
            isLoading: false,
            nameCount: 100,
            isNameExceed: false,
            TEMPLATES,
            continues: false,
            isLoadingTypeList: true,
            isLoadingCategoryList: true,
            isLoadingComponentList: true,
            isLoadingSubComponentList: true,
            typeOptions: {},
            sortedTypeOptions: [],
            file: null,
            mimes: MIMES,
            categories: {},
            categoryOptions: [],
            componentOptions: [
                {
                    value: "",
                    text: "Select subcategory (optional)"
                }
            ],
            subComponentOptions: [
                {
                    value: "",
                    text: "Select subcomponent (optional)"
                }
            ],
            isNext: false,
            vetItemProps: {
                id: "",
                item_type: "11",
                module: "wellness_element",
                status: "",
                start: "",
                source: ""
            },
            planTermOptions: [
                {
                    value: 1,
                    text: "1 Year"
                },
                {
                    value: 2,
                    text: "2 Years"
                },
                {
                    value: 3,
                    text: "3 Years"
                }
            ],
            paymentTermOptions: [
                {
                    value: 1,
                    text: "Monthly in advance"
                },
                {
                    value: 2,
                    text: "Annually in advance"
                }
            ],
            productionCreditOptions: [
                {
                    value: 1,
                    text: "No production credit"
                },
                {
                    value: 2,
                    text: "Earned on enrollment"
                },
                {
                    value: 3,
                    text: "Earned on service provision"
                }
            ],
            enrollmentGoalOptions: [
                {
                    value: 1,
                    text: "New clients"
                },
                {
                    value: 2,
                    text: "New patients"
                },
                {
                    value: 3,
                    text: "Total clients"
                },
                {
                    value: 4,
                    text: "Total patients"
                }
            ],
            selectedEnrollmentFee: null,
            selectedPlanFee: null,
            data: {
                type: null,
                name: "",
                hashtag: "",
                description: "",

                // attachment params
                attachment: "",
                file: null,

                // custom field params
                valuess: "",
                is_multiple: 0,
                allowMultipleSelections: false,
                category: "",
                component: "",
                subComponent: "",

                // wellness plan params
                plan_term: 1,
                enrollment_fee: "",
                enrollment_fee_id: "",
                plan_fee: "",
                plan_fee_id: "",
                payment_terms: 1,
                production_credit: 1,
                enrollment_goal_value: 0,
                enrollment_goal_id: 1
            },
            isEditMode: false,
            edittedTemplate: {},
            isFirstLoadCategory: true,
            isFirstLoadComponent: true,
            displayHashtagAlert: false,
            reservedKeywords: RESERVED_KEYWORDS
        };
    },
    watch: {
        "data.name"(value) {
            let valueLength = 0;
            if (value) {
                valueLength = value.length;
            }

            this.nameCount = 100 - valueLength;

            if (this.nameCount < 0) {
                this.isNameExceed = true;
            } else {
                this.isNameExceed = false;
            }
        },
        "data.file"() {
            let self = this;
            self.file = null;
            if (self.$refs.file) {
                forEach(self.$refs.file.files, function (file) {
                    let ext = file.name.split(".").pop().toLowerCase();
                    if (get(self.mimes, ext)) {
                        const isFileSizePass = self.isMaxFileSize(file);
                        if (isFileSizePass) {
                            self.file = file;
                        } else {
                            self.$refs.file.value = null;
                        }
                    } else {
                        self.$refs.file.value = null;
                        self._message("File type not permitted.", "danger");
                    }
                });
            }
        },
        "data.category"() {
            if (this.isFirstLoadCategory && this.isEditMode) {
                this.isFirstLoadCategory = false;
            } else {
                this.data.component = "";
            }
            this.populateComponentOptions();
        },
        "data.component"() {
            if (this.isFirstLoadComponent && this.isEditMode) {
                this.isFirstLoadComponent = false;
            } else {
                this.data.subComponent = "";
            }
            this.populateSubComponentOptions();
        },
        "data.type"() {
            this.isNext = false;
        },
        "data.hashtag"(value) {
            if (RESERVED_KEYWORDS.includes(value)) {
                this.displayHashtagAlert = true;
            } else {
                this.displayHashtagAlert = false;
            }
        }
    },
    computed: {
        modalTitle() {
            if (this.isNext) {
                return "New Plan";
            } else if (this.isEditMode) {
                return `Edit ${this.templateTypeString}`;
            } else {
                return "New Template";
            }
        },
        currentTemplateType() {
            return this.$route.params.type;
        },
        templateTypeString() {
            if (this.edittedTemplate && this.edittedTemplate.template_type) {
                return this.edittedTemplate.template_type.value;
            } else {
                return TEMPLATES.LIST[this.edittedTemplate.type]?.label ?? "";
            }
        },
        hashtagAlertMessage() {
            const kWords = RESERVED_KEYWORDS.map(e => `"${e}"`);
            const keywords = kWords.join(", ");
            return `${keywords} are reserved keywords`;
        }
    },
    mounted() {},
    methods: {
        open(template) {
            if (template && template.id) {
                this.isEditMode = true;
                this.edittedTemplate = template;

                this.data.id = template.id;
                this.data.name = template.name;
                this.data.description = template.description;

                if (!isEmpty(template.template_custom_field)) {
                    this.data.valuess = template.custom_field_values?.replace(
                        /\s*;\s*/g,
                        "\n"
                    );
                    this.data.is_multiple =
                        template.template_custom_field.is_multiple;
                }
            } else {
                this.isEditMode = false;
                this.edittedTemplate = {};
            }

            this.getTypeList();
            this.getSoapComponentList();
            this.$refs.modalNewTemplate.show();
        },
        async getTypeList() {
            if (this.isEditMode) {
                this.data.type = this.edittedTemplate.type;
            } else {
                try {
                    let response = await TemplateService.getTemplateType();

                    if (response) {
                        this.typeOptions = response.options;

                        if (this.typeOptions) {
                            // Filter out null values before sorting
                            const filteredOptions = Object.entries(
                                this.typeOptions
                            ).filter(
                                ([key, value]) => value != null && value != ""
                            );

                            this.sortedTypeOptions = filteredOptions
                                .map(([key, value]) => ({
                                    value: key,
                                    text: value
                                }))
                                .sort((a, b) => a.text.localeCompare(b.text));

                            this.populateTypeOptions();
                        }
                    }
                } catch (error) {
                    this.isLoadingTypeList = false;
                    console.error(error);
                    this._message("Error: Couldn't get type lists", "danger");
                }
            }
        },
        populateTypeOptions() {
            // SET DEFAULT TYPE ATTACHMENT IF ROUTE CURRENT AT ALL TEMPLATE LIST
            if (this.currentTemplateType) {
                if (
                    this.currentTemplateType ==
                    TEMPLATES.LIST[TEMPLATES.TYPE.ALL].route
                ) {
                    this.data.type = TEMPLATES.TYPE.ATTACHMENT;
                } else {
                    const templateTypeId = parseInt(
                        findKey(TEMPLATES.LIST, {
                            route: this.currentTemplateType
                        })
                    );

                    this.data.type = templateTypeId;
                }

                this.isLoadingTypeList = false;
            }
        },
        async getSoapComponentList() {
            try {
                let response = await TemplateService.getSoapComponentList();

                if (response) {
                    this.categories = response.categories;

                    let category = map(Object.keys(this.categories), item => ({
                        value: item,
                        text: item.split(":")[1]
                    }));

                    this.categoryOptions = category;
                    // Auto select first categoryOptions while component & subComponent empty string
                    // Auto select previous categoryOptions, component & subComponent for isEditMode
                    if (category) {
                        if (!this.isEditMode) {
                            this.data.category = category[0].value;
                        } else {
                            this.data.category = this.edittedTemplate
                                ? this.edittedTemplate.selected_category
                                : "";
                            this.data.component =
                                this.edittedTemplate &&
                                !isEmpty(
                                    this.edittedTemplate.selected_component
                                )
                                    ? this.edittedTemplate.selected_component
                                    : "";
                            this.data.subComponent =
                                this.edittedTemplate &&
                                !isEmpty(
                                    this.edittedTemplate.selected_subcomponent
                                )
                                    ? this.edittedTemplate.selected_subcomponent
                                    : "";
                        }
                        this.isLoadingCategoryList = false;
                    }
                }
            } catch (error) {
                console.error(error);
                this._message(
                    "Error: Couldn't get Medical Note component lists",
                    "danger"
                );
            }
        },
        populateComponentOptions() {
            this.componentOptions.splice(1);

            const selectedCategory = this.data.category;

            // Populate component first selected category
            const components = this.categories[selectedCategory];
            if (components) {
                if (Array.isArray(components)) {
                    map(components, sub =>
                        this.componentOptions.push({
                            value: sub,
                            text: sub.split(":")[1]
                        })
                    );
                } else {
                    map(Object.keys(components), subKey => {
                        if (!Array.isArray(components[subKey])) {
                            this.componentOptions.push({
                                value: components[subKey],
                                text: components[subKey].split(":")[1]
                            });
                        }
                    });
                }
                this.isLoadingComponentList = false;
            }
        },
        populateSubComponentOptions() {
            this.subComponentOptions.splice(1);

            const selectedCategory = this.data.category;
            const selectedComponent = this.data.component;

            const components = this.categories[selectedCategory];
            if (components) {
                const subComponents = components[selectedComponent];
                if (subComponents) {
                    if (Array.isArray(subComponents)) {
                        map(subComponents, sub =>
                            this.subComponentOptions.push({
                                value: sub,
                                text: sub.split(":")[1]
                            })
                        );
                    } else {
                        map(Object.keys(subComponents), subKey => {
                            if (!Array.isArray(subComponents[subKey])) {
                                this.subComponentOptions.push({
                                    value: subComponents[subKey],
                                    text: subComponents[subKey].split(":")[1]
                                });
                            }
                        });
                    }
                    this.isLoadingSubComponentList = false;
                }
            }
        },
        async proceed(repeat) {
            try {
                let selectedTemplateType = parseInt(this.data.type);
                let valid = await this.$validator.validateAll();

                if (
                    (selectedTemplateType == TEMPLATES.TYPE.WEBFORM_TEMPLATE ||
                        selectedTemplateType == TEMPLATES.TYPE.WELLNESS_PLAN) &&
                    (!valid || this.isNameExceed)
                ) {
                    return false;
                }

                this.continues = repeat;
                this.isLoading = true;

                let params = {
                    name:
                        selectedTemplateType != TEMPLATES.TYPE.WELLNESS_PLAN
                            ? this.data.name
                            : "",
                    description:
                        selectedTemplateType != TEMPLATES.TYPE.CUSTOM_FIELD
                            ? this.data.description
                            : "",
                    type: selectedTemplateType
                };

                switch (selectedTemplateType) {
                    case TEMPLATES.TYPE.CONTENT_SNIPPET:
                        params.hashtag = this.data.hashtag;
                        break;

                    case TEMPLATES.TYPE.CUSTOM_FIELD:
                        params.valuess = this.data.valuess;
                        params.is_multiple = this.data.is_multiple;
                        params.category = this.data.category;
                        params.component = this.data.component;
                        params.sub_component = this.data.subComponent;
                        break;

                    case TEMPLATES.TYPE.WELLNESS_PLAN:
                        params.plan_name = this.data.name;
                        params.plan_term = this.data.plan_term;
                        params.enrollment_fee =
                            this.selectedEnrollmentFee?.display_name ?? "";
                        params.enrollment_fee_item_id =
                            this.selectedEnrollmentFee?.id ?? "";
                        params.plan_fee = this.selectedPlanFee?.display_name;
                        params.plan_fee_item_id = this.selectedPlanFee?.id;
                        params.payment_terms = this.data.payment_terms;
                        params.production_credit = this.data.production_credit;
                        params.enrollment_goal_value = this.data.enrollment_goal_value;
                        params.enrollment_goal_id = this.data.enrollment_goal_id;
                        break;

                    default:
                        break;
                }

                let res;

                if (this.isEditMode) {
                    let res = await TemplateService.put(
                        this.edittedTemplate.id,
                        params
                    );

                    if (res && res.id) {
                        this._message("Edits saved successfully.");
                        this.close();
                        this.$emit("onsuccess");
                    } else {
                        throw new Error(
                            res && res.message
                                ? res.message
                                : "Error: Unable to edit template"
                        );
                    }
                } else {
                    res = await TemplateService.post(params);

                    if (res && res.id) {
                        if (
                            this.file &&
                            selectedTemplateType == TEMPLATES.TYPE.ATTACHMENT
                        ) {
                            let fileObj = this.file;
                            let businessId = this._setting.business_id;

                            const attachmentFilePath = `${this._s3Path(
                                "business",
                                businessId
                            )}attachments`;

                            const resStoreS3 = await this.storeS3(
                                [{ file: fileObj }],
                                {
                                    name: "business",
                                    id: res.id,
                                    path: attachmentFilePath
                                }
                            );

                            if (
                                resStoreS3 &&
                                Array.isArray(resStoreS3) &&
                                resStoreS3[0].path
                            ) {
                                await this.addAttachment(res.id, resStoreS3[0]);
                            }
                        }

                        if (
                            repeat &&
                            (this.data.type == TEMPLATES.TYPE.ATTACHMENT ||
                                this.data.type == TEMPLATES.TYPE.CUSTOM_FIELD)
                        ) {
                            this.$validator.reset();
                            this.resetGeneralFields();
                            this.getSoapComponentList();
                        } else {
                            this.close();
                        }

                        this.reloadRedirectPage(res);
                    } else {
                        throw new Error(
                            res && res.message
                                ? res.message
                                : "Error: Unable to create new template "
                        );
                    }
                }

                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                console.error(error.message);
                this._message("Error: Unable to create new template", "danger");
            }
        },
        reloadRedirectPage(res) {
            let templateTypeDetails = this.data.type;

            if (
                templateTypeDetails == TEMPLATES.TYPE.ATTACHMENT ||
                templateTypeDetails == TEMPLATES.TYPE.CUSTOM_FIELD
            ) {
                // Attachment & Custom Field => If not at the list page redirect
                if (
                    this.currentTemplateType !=
                    TEMPLATES.LIST[templateTypeDetails].route
                ) {
                    this.$router.push({
                        name: "template_list",
                        params: {
                            type: TEMPLATES.LIST[templateTypeDetails].route
                        }
                    });
                } else {
                    // Attachment & Custom Field => Else Reload Page upon save
                    this.$emit("onsuccess");
                }
            } else if (
                TEMPLATES.IN_VIEW.includes(Number(templateTypeDetails))
            ) {
                // Redirect to view page
                this.$router.push({
                    name: "template_details",
                    params: {
                        type: TEMPLATES.LIST[templateTypeDetails].route,
                        id: res.encoded_id
                    }
                });
            } else {
                // Else => redirect to CI page
                window.location.href =
                    process.env.CI_APPS +
                    "business/template/view/" +
                    res.encoded_id;
            }
        },
        resetGeneralFields() {
            this.data.name = "";
            this.data.description = "";
            this.data.valuess = "";
            this.data.is_multiple = "";
            this.data.category = "";
            this.data.component = "";
            this.data.subComponent = "";
            this.file = null;
            this.data.file = null;
            if (this.$refs.file) {
                this.$refs.file.value = null;
            }
        },
        async addAttachment(templateId, resStoreS3) {
            const params = {
                title: resStoreS3.title,
                filename: `${resStoreS3.filename}.${resStoreS3.ext}`,
                ext: resStoreS3.ext,
                summary: this.data.description,
                isactive: 1,
                is_dicom: 0,
                is_s3: 1,
                template_id: templateId
            };

            try {
                const res = await AttachmentService.updateAttachment(params);

                if (!res || (res && res.message && res.message.code != 200)) {
                    throw new Error("Error: Unable to save attachment");
                }
            } catch (error) {
                this.isLoading = false;
                console.error(error.message);
                this._message("Error: Unable to save attachment", "danger");
            }
        },
        close() {
            this.$refs.modalNewTemplate.hide();
        }
    }
};
</script>
